import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import ChartContainer from '../vedic/results/ChartContainer';
import ScoreRing from '../vedic/results/ScoreRing';
import KootaGrid from '../vedic/results/KootaGrid';
import LoadingState from '../vedic/feedback/LoadingState';
import { freeKundliService } from '../../services/api';
import { getLocale, availableLanguages } from '../../locales';
import { useAnalytics } from '../../analytics/hooks';
import MatchingForm from '../vedic/forms/MatchingForm';
import { to24Hour } from '../vedic/forms/TimeInput';
import ErrorAlert from '../vedic/feedback/ErrorAlert';

/* ─── Styled Components ─── */

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 40px auto;
  padding: 0 20px 60px;
`;

const PageTitle = styled.h1`
  font-size: 2rem;
  text-align: center;
  margin-bottom: 10px;
  color: ${p => p.theme.colors.text.primary};
`;

const PageSubtitle = styled.p`
  text-align: center;
  color: ${p => p.theme.colors.text.secondary};
  margin-bottom: 30px;
  font-size: 0.95rem;
`;

const BackLink = styled.button`
  background: none;
  border: none;
  color: ${p => p.theme.colors.bands.exceptional};
  font-size: 0.95rem;
  cursor: pointer;
  margin-bottom: 20px;
  padding: 0;
  &:hover { text-decoration: underline; }
`;

const Card = styled.div`
  background: ${p => p.theme.colors.surface};
  border-radius: ${p => p.theme.radius.md}px;
  box-shadow: ${p => p.theme.shadow.card};
  padding: 30px;
`;

const CardTitle = styled.h2`
  font-size: 1.3rem;
  margin-bottom: 20px;
  color: ${props => props.color || props.theme.colors.text.primary};
  text-align: center;
`;

const LangToggle = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 4px;
  margin-bottom: 16px;
`;

const LangBtn = styled.button`
  padding: 6px 14px;
  border: 1px solid ${props => props.active ? props.theme.colors.bands.exceptional : props.theme.colors.border};
  background: ${props => props.active ? props.theme.colors.bands.exceptional : props.theme.colors.surface};
  color: ${props => props.active ? props.theme.colors.surface : props.theme.colors.text.secondary};
  border-radius: ${p => p.theme.radius.sm}px;
  font-size: 0.85rem;
  font-weight: ${props => props.active ? '600' : '400'};
  cursor: pointer;
  transition: all 0.2s;
  &:hover { border-color: ${p => p.theme.colors.bands.exceptional}; }
`;

/* ─── Result Components ─── */

const VerdictText = styled.div`
  text-align: center;
  font-size: 1.1rem;
  font-weight: 600;
  margin-top: 8px;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: ${props => props.verdictColor || props.theme.colors.text.secondary};
`;

const RecommendationsList = styled.div`
  margin-top: 20px;
  ul {
    list-style: none;
    padding: 0;
    li {
      padding: 10px 16px;
      margin-bottom: 8px;
      background: #fff3e0;
      border-radius: 6px;
      font-size: 0.9rem;
      border-left: 3px solid ${p => p.theme.colors.bands.weak};
    }
  }
`;

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
  margin-top: 30px;
  @media (max-width: 992px) { grid-template-columns: 1fr; }
`;

const MangalCard = styled.div`
  margin-top: 20px;
  padding: 16px 20px;
  border-radius: ${p => p.theme.radius.md}px;
  background: ${props => props.match ? '#e8f5e9' : props.theme.colors.error.bg};
  border-left: 4px solid ${props => props.match ? props.theme.colors.bands.strong : props.theme.colors.bands.challenging};
  .title { font-weight: 600; margin-bottom: 6px; }
  .detail { font-size: 0.9rem; color: ${p => p.theme.colors.text.secondary}; }
`;

/* ─── Main Component ─── */

const KundliMatching = () => {
  const navigate = useNavigate();
  const { track } = useAnalytics();
  const [lang, setLang] = useState('en');
  const loc = getLocale(lang);
  const t = loc.matching;
  const [person1Values, setPerson1Values] = useState({
    dob: '', hour: '', minute: '', period: 'AM', timeFormat: '12h',
    place: '', latitude: null, longitude: null, timezone: 'Asia/Kolkata',
  });
  const [person2Values, setPerson2Values] = useState({
    dob: '', hour: '', minute: '', period: 'AM', timeFormat: '12h',
    place: '', latitude: null, longitude: null, timezone: 'Asia/Kolkata',
  });

  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const parseDob = (dob) => {
    const parts = dob.split('-');
    if (parts.length !== 3) return null;
    return { year: parseInt(parts[0]), month: parseInt(parts[1]), day: parseInt(parts[2]) };
  };

  const handleSubmit = useCallback(async (brideVals, groomVals) => {
    setError(null);

    if (!brideVals.latitude || !groomVals.latitude) {
      setError(t.errSelectPlace);
      return;
    }

    const brideDob = parseDob(brideVals.dob);
    const groomDob = parseDob(groomVals.dob);
    if (!brideDob || !groomDob) {
      setError(t.errInvalidDob);
      return;
    }

    setLoading(true);
    track('kundli_started', { chart_type: 'matching' });
    try {
      const payload = {
        bride: {
          year: brideDob.year, month: brideDob.month, day: brideDob.day,
          hour: to24Hour(parseInt(brideVals.hour || '0'), brideVals.period || 'AM', brideVals.timeFormat || '12h'),
          minute: parseInt(brideVals.minute || '0'),
          latitude: brideVals.latitude, longitude: brideVals.longitude,
        },
        groom: {
          year: groomDob.year, month: groomDob.month, day: groomDob.day,
          hour: to24Hour(parseInt(groomVals.hour || '0'), groomVals.period || 'AM', groomVals.timeFormat || '12h'),
          minute: parseInt(groomVals.minute || '0'),
          latitude: groomVals.latitude, longitude: groomVals.longitude,
        },
        timezone: brideVals.timezone || 'Asia/Kolkata',
      };

      const res = await freeKundliService.calculateMatching(payload);
      track('kundli_completed', { chart_type: 'matching' });
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to calculate matching. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [t, track]);

  const matching = result?.matching;

  return (
    <PageContainer>
      <LangToggle>
        {availableLanguages.map(l => (
          <LangBtn key={l.code} active={lang === l.code} onClick={() => setLang(l.code)}>
            {l.label}
          </LangBtn>
        ))}
      </LangToggle>

      <BackLink onClick={() => navigate('/free-kundli')}>&larr; {t.backToKundli}</BackLink>
      <PageTitle>{t.title}</PageTitle>
      <PageSubtitle>{t.subtitle}</PageSubtitle>

      <MatchingForm
        person1Label={t.brideDetails}
        person2Label={t.groomDetails}
        person1Color="#e91e63"
        person2Color="#2196f3"
        fields={['dob', 'time', 'place']}
        person1Values={person1Values}
        person2Values={person2Values}
        onPerson1Change={setPerson1Values}
        onPerson2Change={setPerson2Values}
        onSubmit={handleSubmit}
        submitLabel={loading ? t.calculating : t.calculateMatching}
        loading={loading}
        pageName="kundli_matching"
        lang={lang}
      />

      <ErrorAlert message={error} onDismiss={() => setError(null)} />
      <LoadingState loading={loading} variant="score" />

      {/* Results */}
      {matching && (
        <>
          {/* Score Ring */}
          <div style={{ margin: '30px 0', textAlign: 'center' }}>
            <ScoreRing
              value={matching.total_points}
              max={matching.max_points}
              color={matching.total_points >= 28 ? '#4caf50' : matching.total_points >= 21 ? '#ff9800' : matching.total_points >= 17 ? '#f57f17' : '#f44336'}
              size={180}
              label={`${matching.total_points} / ${matching.max_points}`}
            />
            <VerdictText verdictColor={matching.verdict_color || '#666'}>
              {(loc.verdicts && loc.verdicts[matching.verdict]) || matching.verdict}
            </VerdictText>
          </div>

          {/* Kundli Charts */}
          <ChartsGrid>
            <Card>
              <ChartContainer chartData={result.bride_chart} title={t.brideChart} loc={loc} />
            </Card>
            <Card>
              <ChartContainer chartData={result.groom_chart} title={t.groomChart} loc={loc} />
            </Card>
          </ChartsGrid>

          {/* Koota Breakdown */}
          <Card>
            <CardTitle>{t.kootaBreakdown}</CardTitle>
            <KootaGrid
              expandable
              lang={lang}
              items={matching.kootas.map(k => ({
                name: (loc.kootaNames && loc.kootaNames[k.name]) || k.name,
                nameTa: k.name_tamil,
                score: k.score,
                maxScore: k.max_points,
                pass: k.score > 0,
                color: k.color,
                description: loc.kootas[k.name] || k.description || '',
              }))}
            />
          </Card>

          {/* Mangal Dosha Cross-check */}
          {matching.mangal_cross_check && (
            <MangalCard match={matching.mangal_cross_check.compatible}>
              <div className="title">{t.mangalCrossCheck}</div>
              <div className="detail">
                {t.bride}: {matching.mangal_cross_check.bride_mangal ? t.manglik : t.nonManglik} |{' '}
                {t.groom}: {matching.mangal_cross_check.groom_mangal ? t.manglik : t.nonManglik}
                {matching.mangal_cross_check.compatible
                  ? ` — ${t.compatible}`
                  : ` — ${t.mismatch}`}
              </div>
            </MangalCard>
          )}

          {/* Recommendations */}
          {matching.recommendations && matching.recommendations.length > 0 && (
            <RecommendationsList>
              <Card>
                <CardTitle>{t.recommendations}</CardTitle>
                <ul>
                  {matching.recommendations.map((r, i) => (
                    <li key={i}>{(loc.matchingRecommendations && loc.matchingRecommendations[r]) || r}</li>
                  ))}
                </ul>
              </Card>
            </RecommendationsList>
          )}

        </>
      )}
    </PageContainer>
  );
};

export default KundliMatching;
